import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'supertrend',
  name: 'Supertrend',
  shortName: 'ST',
  category: 'trend',
  overlay: true,
  params: [
    { key: 'period', label: 'ATR Period', type: 'number', default: 10, min: 1, max: 200 },
    {
      key: 'multiplier',
      label: 'Multiplier',
      type: 'number',
      default: 3,
      min: 0.1,
      max: 20,
      step: 0.1,
    },
  ],
  lines: [
    { key: 'bull', label: 'Bull', color: '#26a69a', width: 2 },
    { key: 'bear', label: 'Bear', color: '#ef5350', width: 2 },
  ],
}

class Supertrend extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const period = Math.max(1, Number(params['period']) || 10)
    const mult = Number(params['multiplier']) || 3

    const n = candles.length
    const bull: (number | null)[] = new Array<number | null>(n).fill(null)
    const bear: (number | null)[] = new Array<number | null>(n).fill(null)

    if (n <= period)
      return {
        lines: [
          { key: 'bull', values: bull },
          { key: 'bear', values: bear },
        ],
      }

    // Wilder's ATR
    let atr = 0
    for (let i = 0; i < period; i++) {
      const tr =
        i === 0
          ? candles[i].h - candles[i].l
          : Math.max(
              candles[i].h - candles[i].l,
              Math.abs(candles[i].h - candles[i - 1].c),
              Math.abs(candles[i].l - candles[i - 1].c)
            )
      atr += tr
    }
    atr /= period

    // Final upper/lower band values from previous bar
    let finalUpper = 0
    let finalLower = 0
    let trend = 1 // 1 = bullish, -1 = bearish

    for (let i = period; i < n; i++) {
      const tr = Math.max(
        candles[i].h - candles[i].l,
        Math.abs(candles[i].h - candles[i - 1].c),
        Math.abs(candles[i].l - candles[i - 1].c)
      )
      atr = (atr * (period - 1) + tr) / period

      const hl2 = (candles[i].h + candles[i].l) / 2
      const basicUpper = hl2 + mult * atr
      const basicLower = hl2 - mult * atr

      // Ratchet the bands: upper only moves down, lower only moves up
      const newUpper =
        i === period || basicUpper < finalUpper || candles[i - 1].c > finalUpper
          ? basicUpper
          : finalUpper
      const newLower =
        i === period || basicLower > finalLower || candles[i - 1].c < finalLower
          ? basicLower
          : finalLower

      // Trend switch logic
      if (trend === 1 && candles[i].c < newLower) trend = -1
      else if (trend === -1 && candles[i].c > newUpper) trend = 1

      finalUpper = newUpper
      finalLower = newLower

      if (trend === 1) bull[i] = finalLower
      else bear[i] = finalUpper
    }

    return {
      lines: [
        { key: 'bull', values: bull },
        { key: 'bear', values: bear },
      ],
    }
  }
}

registerIndicator(Supertrend, CONFIG)
